import React, { useEffect, useRef, useState } from 'react';
import './AdPlaceholder.css';

interface AdPlaceholderProps {
    adSlot?: string;
    format?: 'horizontal' | 'rectangle' | 'vertical' | 'auto';
    testMode?: boolean; // Enable test ads
    forceShow?: boolean; // Override the 10-view limit for testing
}

const AD_VIEW_COUNTER_KEY = 'psm_ad_view_count';
const AD_SHOW_FREQUENCY = 10; // Show ad every 10th view

/**
 * Increments the page view counter and returns whether ads should be shown
 * Ads are shown every 10th view (views 10, 20, 30, etc.)
 */
const shouldShowAd = (forceShow?: boolean): boolean => {
    if (forceShow) return true;

    try {
        const stored = localStorage.getItem(AD_VIEW_COUNTER_KEY);
        const currentCount = stored ? parseInt(stored, 10) : 0;
        const newCount = currentCount + 1;

        localStorage.setItem(AD_VIEW_COUNTER_KEY, newCount.toString());

        // Show ad every 10th view
        return newCount % AD_SHOW_FREQUENCY === 0;
    } catch (e) {
        console.warn('Failed to access localStorage for ad counter', e);
        return false;
    }
};

export const AdPlaceholder: React.FC<AdPlaceholderProps> = ({
    adSlot = '',
    format = 'horizontal',
    testMode = false, // Set to true to see test ads
    forceShow = false // Override frequency check
}) => {
    const adInsRef = useRef<HTMLModElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [adStatus, setAdStatus] = useState<'loading' | 'filled' | 'unfilled'>('loading');
    const [debugInfo, setDebugInfo] = useState<string[]>([]);
    const [showAdSlot, setShowAdSlot] = useState(shouldShowAd(forceShow));

    // Publisher ID is hardcoded in index.html head tag
    const publisherId = 'ca-pub-5295328688227542';

    const addDebug = (message: string) => {
        console.log(`[AdPlaceholder] ${message}`);
        setDebugInfo(prev => [...prev, message]);
    };

    useEffect(() => {
        // Skip initialization if we shouldn't show ads on this view
        if (!showAdSlot) {
            addDebug('⏭️ Skipping ad initialization (not a show cycle - 1 of 10 views)');
            setAdStatus('unfilled');
            return;
        }

        const adElement = adInsRef.current;
        if (!adElement) {
            addDebug('ERROR: Ad element ref not found');
            return;
        }

        addDebug(`Initializing ad... Test mode: ${testMode}`);
        addDebug(`Ad slot: ${adSlot || 'Auto Ads (no slot)'}`);
        addDebug(`Format: ${format}`);
        addDebug(`✅ This is an ad show cycle (every 10th view)`);

        const isManualAd = adSlot && adSlot.trim() !== '';

        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' && mutation.attributeName === 'data-ad-status') {
                    const status = adElement.getAttribute('data-ad-status');
                    addDebug(`Ad status changed to: ${status}`);
                    
                    if (status === 'filled') {
                        setAdStatus('filled');
                        addDebug('✅ Ad filled successfully!');
                        clearTimeout(fallbackTimeout);
                        observer.disconnect();
                    }
                    if (status === 'unfilled') {
                        setAdStatus('unfilled');
                        addDebug('❌ Ad unfilled (no ad available)');
                        clearTimeout(fallbackTimeout);
                        observer.disconnect();
                    }
                }
            });
        });

        observer.observe(adElement, { attributes: true, attributeFilter: ['data-ad-status'] });

        const fallbackTimeout = setTimeout(() => {
            const currentStatus = adElement.getAttribute('data-ad-status');
            addDebug(`Fallback timeout triggered. Current status: ${currentStatus}`);
            
            if (currentStatus !== 'filled') {
                addDebug('⏱️ Timeout: Ad did not load in 3 seconds');
                setAdStatus('unfilled');
                observer.disconnect();
            }
        }, 3000);
        
        // Check if AdSense script is loaded
        if (!(window as any).adsbygoogle) {
            addDebug('⚠️ WARNING: adsbygoogle not found! Script may not be loaded.');
        }

        // Push to AdSense
        try {
            addDebug('Pushing to adsbygoogle...');
            ((window as any).adsbygoogle = (window as any).adsbygoogle || []).push({});
            addDebug('Push successful');
        } catch (err) {
            addDebug(`❌ ERROR pushing to adsbygoogle: ${err}`);
            console.error('AdSense push error:', err);
            setAdStatus('unfilled');
            clearTimeout(fallbackTimeout);
            observer.disconnect();
        }

        return () => {
            clearTimeout(fallbackTimeout);
            try { observer.disconnect(); } catch (e) {}
        };
    }, [adSlot, testMode, format, showAdSlot]);

    const getContainerStyle = (): React.CSSProperties => {
        switch (adStatus) {
            case 'filled':
                return {
                    opacity: 1,
                    transition: 'opacity 0.5s ease-in',
                };
            case 'unfilled':
                return {
                    height: 0,
                    minHeight: 0,
                    margin: 0,
                    padding: 0,
                    border: 'none',
                    overflow: 'hidden',
                    opacity: 0,
                    transition: 'all 0.3s ease-out',
                };
            case 'loading':
            default:
                return {
                    opacity: 1,
                };
        }
    };

    const getAdFormatStyle = () => {
        switch (format) {
            case 'horizontal':
                return 'horizontal';
            case 'rectangle':
                return 'rectangle';
            case 'vertical':
                return 'vertical';
            case 'auto':
            default:
                return 'auto';
        }
    };

    return (
        <div 
            ref={containerRef}
            className="ad-placeholder" 
            style={getContainerStyle()}
            data-ad-placement="in-page"
        >
            {adStatus === 'loading' && (
                <div className="ad-loading-text">
                    loading advertisement...
                    {testMode && <span style={{display: 'block', fontSize: '0.8em', marginTop: '8px'}}>
                        🧪 TEST MODE ENABLED
                    </span>}
                </div>
            )}
            
            {/* Debug info - remove this in production */}
            {testMode && adStatus === 'unfilled' && (
                <div style={{
                    padding: '10px',
                    background: '#fff3cd',
                    border: '1px solid #ffc107',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontFamily: 'monospace',
                    textAlign: 'left',
                    maxWidth: '728px',
                    margin: '10px auto'
                }}>
                    <strong>Debug Info:</strong>
                    <ul style={{margin: '5px 0', paddingLeft: '20px'}}>
                        {debugInfo.map((info, i) => (
                            <li key={i}>{info}</li>
                        ))}
                    </ul>
                </div>
            )}
            
            <div style={{
                width: '100%',
                maxWidth: '728px',
                margin: adStatus !== 'unfilled' ? '20px auto' : '0',
                display: adStatus === 'filled' ? 'block' : 'none',
            }}>
                <ins
                    ref={adInsRef}
                    className="adsbygoogle"
                    style={{ display: 'block', textAlign: 'center' }}
                    data-ad-client={publisherId}
                    {...(adSlot && { 'data-ad-slot': adSlot })}
                    data-ad-format={getAdFormatStyle()}
                    data-full-width-responsive="true"
                    data-ad-category="games"
                    {...(testMode && { 'data-adtest': 'on' })} // Test mode
                />
            </div>
        </div>
    );
};